/**
 * ─────────────────────────────────────────────────────────────────────
 * FILE: src/pages/Dashboard.tsx
 * DESCRIPTION: Main dashboard with stats, analytics charts, and recent 
 *              activity. Provides overview of media verification 
 *              activities, AI assessments, and risk alerts.
 *
 * FEATURES:
 * - Key metrics cards with trends
 * - AI vs Human-Created content trends (area chart)
 * - Content type distribution (pie chart)
 * - Recent activity table with sorting
 * - Action menus for each file
 * - Dark/Light mode support (via Header)
 * - User profile integration (via Header)
 *
 * DATA INTEGRATION:
 * - Mock data structured for easy replacement with API calls
 * - All sorting and filtering functional
 * - Charts ready for live data
 * - Action handlers prepared for backend integration
 *
 * @copyright 2026-2026 Anou. All rights reserved.
 * @author Anou
 * @version 2.0.0
 * @created 2026-01-15
 * @updated 2026-03-26 - Rewritten with new design, removed header code,
 *                       added sortable tables, fixed statuses, production-ready
 * ─────────────────────────────────────────────────────────────────────
 */

import { useState, useEffect } from 'react';
import { useUser, useOrganization } from '@clerk/react';
import { useNavigate } from 'react-router-dom';
import {
  Upload,
  FileCheck,
  Cpu,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  MoreVertical,
  Eye,
  Download,
  Trash2,
  Share2,
  ChevronUp,
  ChevronDown,
  Copy,
  Check,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';

// ─────────────────────────────────────────────────────────────────────
// TYPES - Structured for easy API integration
// ─────────────────────────────────────────────────────────────────────
interface StatCard {
  label: string;
  value: string | number;
  sublabel: string;
  trend: 'up' | 'down' | 'neutral';
  trendValue: string;
  icon: React.ElementType;
  color: 'navy' | 'green' | 'blue' | 'red';
  bgColor: string;
}

interface TrendData {
  time: string;
  ai: number;
  human: number;
}

interface ContentDistribution {
  name: string;
  value: number;
  color: string;
}

interface RecentFile {
  id: string;
  fileName: string;
  type: 'Document' | 'Image' | 'Video' | 'Audio' | 'File';
  trustScore: number;
  status: 'verified' | 'processing' | 'failed' | 'risk-detected' | 'pending';
  date: string;
  size: string;
  persistentId: string;
}

type SortField = 'fileName' | 'type' | 'trustScore' | 'status' | 'date';
type SortOrder = 'asc' | 'desc';

// ─────────────────────────────────────────────────────────────────────
// MOCK DATA - Remove this section when connecting to live API
// ─────────────────────────────────────────────────────────────────────
const MOCK_STATS: StatCard[] = [
  {
    label: 'Total Uploads',
    value: '1,433',
    sublabel: '550 Uploads',
    trend: 'up',
    trendValue: '+12%',
    icon: Upload,
    color: 'navy',
    bgColor: 'bg-slate-800',
  },
  {
    label: 'Verification Volume',
    value: '665',
    sublabel: 'Verified Content',
    trend: 'up',
    trendValue: '+8%',
    icon: FileCheck,
    color: 'green',
    bgColor: 'bg-emerald-500',
  },
  {
    label: 'Volume AI Assessement',
    value: '47%',
    sublabel: 'AI assessments',
    trend: 'up',
    trendValue: '+5%',
    icon: Cpu,
    color: 'blue',
    bgColor: 'bg-blue-500',
  },
  {
    label: 'Total Risky content',
    value: '0',
    sublabel: 'Risk Alerts',
    trend: 'down',
    trendValue: '-2%',
    icon: AlertTriangle,
    color: 'red',
    bgColor: 'bg-red-50',
  },
];

const MOCK_TREND_DATA: TrendData[] = [
  { time: 'Tim 1', ai: 100, human: 80 },
  { time: 'Tim 2', ai: 280, human: 220 },
  { time: 'Tim 3', ai: 250, human: 300 },
  { time: 'Tim 4', ai: 450, human: 280 },
  { time: 'Tim 5', ai: 380, human: 550 },
  { time: 'Tim 6', ai: 520, human: 480 },
  { time: 'Time', ai: 650, human: 600 },
];

const MOCK_CONTENT_DISTRIBUTION: ContentDistribution[] = [
  { name: 'Video', value: 40, color: '#3182ce' },
  { name: 'Image', value: 58, color: '#38a169' },
  { name: 'Audio', value: 23, color: '#63b3ed' },
];

const MOCK_RECENT_FILES: RecentFile[] = [
  {
    id: '1',
    fileName: 'VeriMark_Intro_Video_01.mp4',
    type: 'Video',
    trustScore: 7.0,
    status: 'verified',
    date: '2026-03-28',
    size: '45MB',
    persistentId: 'VM7381A',
  },
  {
    id: '2',
    fileName: 'Product_Launch_Draft.png',
    type: 'Image',
    trustScore: 6.4,
    status: 'verified',
    date: '2026-03-28',
    size: '1.2MB',
    persistentId: 'VM7382A',
  },
  {
    id: '3',
    fileName: 'Team_Meeting_Recording.wav',
    type: 'Audio',
    trustScore: 5.0,
    status: 'processing',
    date: '2026-03-28',
    size: '8.5MB',
    persistentId: 'VM7383A',
  },
  {
    id: '4',
    fileName: 'Marketing_Campaign.pdf',
    type: 'Document',
    trustScore: 8.2,
    status: 'verified',
    date: '2026-03-27',
    size: '2.3MB',
    persistentId: 'VM7384A',
  },
  {
    id: '5',
    fileName: 'Brand_Logo_Final.svg',
    type: 'Image',
    trustScore: 3.2,
    status: 'risk-detected',
    date: '2026-03-27',
    size: '0.5MB',
    persistentId: 'VM7385A',
  },
  {
    id: '6',
    fileName: 'Customer_Testimonial.mp4',
    type: 'Video',
    trustScore: 0,
    status: 'pending',
    date: '2026-03-27',
    size: '125MB',
    persistentId: 'VM7386A',
  },
  {
    id: '7',
    fileName: 'Financial_Report_Q2.xlsx',
    type: 'Document',
    trustScore: 9.1,
    status: 'verified',
    date: '2026-03-26',
    size: '3.8MB',
    persistentId: 'VM7387A',
  },
  {
    id: '8',
    fileName: 'Corrupted_File.dat',
    type: 'File',
    trustScore: 0,
    status: 'failed',
    date: '2026-03-26',
    size: '0.1MB',
    persistentId: 'VM7388A',
  },
];

// ─────────────────────────────────────────────────────────────────────
// UTILITY FUNCTIONS - Keep these, they work with live data too
// ─────────────────────────────────────────────────────────────────────
const getStatusBadge = (status: RecentFile['status']) => {
  const badges = {
    verified: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
    processing: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    failed: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    'risk-detected': 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    pending: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-400',
  };
  return badges[status];
};

const getStatusLabel = (status: RecentFile['status']) => {
  const labels = {
    verified: 'Verified',
    processing: 'Processing',
    failed: 'Failed',
    'risk-detected': 'Risk Detected',
    pending: 'Pending',
  };
  return labels[status];
};

const getTrustScoreColor = (score: number) => {
  if (score >= 7) return 'text-emerald-600 dark:text-emerald-400';
  if (score >= 5) return 'text-yellow-600 dark:text-yellow-400';
  if (score > 0) return 'text-red-600 dark:text-red-400';
  return 'text-gray-400';
};

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const formatNumber = (num: number | string): string => {
  if (typeof num === 'string') return num;
  return num.toLocaleString();
};

// ─────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────
const Dashboard: React.FC = () => {
  // ───────────────────────────────────────────────────────────────────
  // STATE
  // ───────────────────────────────────────────────────────────────────
  const { user } = useUser();
  const { organization } = useOrganization();
  const navigate = useNavigate();
  
  const [recentFiles, setRecentFiles] = useState<RecentFile[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [sortField, setSortField] = useState<SortField>('date');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [activeMenuId, setActiveMenuId] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // ───────────────────────────────────────────────────────────────────
  // EFFECTS
  // ───────────────────────────────────────────────────────────────────
  useEffect(() => {
    // TODO: Replace with actual API call
    // Example: fetch('/api/dashboard/recent-files').then(res => res.json()).then(setRecentFiles)
    const loadData = async () => {
      setIsLoading(true);
      await new Promise(resolve => setTimeout(resolve, 500)); // Simulate API delay
      setRecentFiles(MOCK_RECENT_FILES);
      setIsLoading(false);
    };
    loadData();
  }, []);

  // ───────────────────────────────────────────────────────────────────
  // HANDLERS
  // ───────────────────────────────────────────────────────────────────
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  const sortedFiles = [...recentFiles].sort((a, b) => {
    let comparison = 0;
    
    switch (sortField) {
      case 'fileName':
        comparison = a.fileName.localeCompare(b.fileName);
        break;
      case 'type':
        comparison = a.type.localeCompare(b.type);
        break;
      case 'trustScore':
        comparison = a.trustScore - b.trustScore;
        break;
      case 'status':
        comparison = a.status.localeCompare(b.status);
        break;
      case 'date':
        comparison = new Date(a.date).getTime() - new Date(b.date).getTime();
        break;
    }
    
    return sortOrder === 'asc' ? comparison : -comparison;
  });

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleViewFile = (fileId: string) => {
    navigate(`/identities/${fileId}`);
    setActiveMenuId(null);
  };

  const handleDownload = (file: RecentFile) => {
    // TODO: Implement actual download
    console.log('Downloading:', file.fileName);
    alert(`Download started: ${file.fileName}`);
    setActiveMenuId(null);
  };

  const handleDelete = (fileId: string) => {
    if (window.confirm('Are you sure you want to delete this file?')) {
      // TODO: Implement actual delete API call
      setRecentFiles(prev => prev.filter(f => f.id !== fileId));
      setActiveMenuId(null);
    }
  };

  const handleShare = (file: RecentFile) => {
    const shareLink = `${window.location.origin}/identities/${file.id}`;
    navigator.clipboard.writeText(shareLink);
    alert('Share link copied to clipboard!');
    setActiveMenuId(null);
  };

  // ───────────────────────────────────────────────────────────────────
  // RENDER
  // ───────────────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4" />
          <div className="grid grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-32 bg-gray-200 dark:bg-gray-700 rounded" />
            ))}
          </div>
          <div className="grid grid-cols-3 gap-6">
            <div className="col-span-2 h-80 bg-gray-200 dark:bg-gray-700 rounded" />
            <div className="h-80 bg-gray-200 dark:bg-gray-700 rounded" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Page Title */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Dashboard</h1>
        <p className="text-gray-600 dark:text-gray-400">
          Welcome back! Here's what's happening with your media verification.
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {MOCK_STATS.map((stat) => (
          <div
            key={stat.label}
            className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700"
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">{stat.label}</p>
                <p className="text-sm text-gray-500 dark:text-gray-500">{stat.sublabel}</p>
              </div>
              <div className={`p-3 rounded-lg ${stat.bgColor}`}>
                <stat.icon
                  className={`w-6 h-6 ${
                    stat.color === 'navy' ? 'text-white' : 'text-white'
                  }`}
                />
              </div>
            </div>
            <div className="flex items-end justify-between">
              <p className="text-3xl font-bold text-gray-900 dark:text-white">
                {formatNumber(stat.value)}
              </p>
              <div
                className={`flex items-center gap-1 text-sm ${
                  stat.trend === 'up'
                    ? 'text-emerald-600 dark:text-emerald-400'
                    : stat.trend === 'down'
                    ? 'text-red-600 dark:text-red-400'
                    : 'text-gray-600 dark:text-gray-400'
                }`}
              >
                {stat.trend === 'up' ? (
                  <TrendingUp className="w-4 h-4" />
                ) : stat.trend === 'down' ? (
                  <TrendingDown className="w-4 h-4" />
                ) : null}
                <span>{stat.trendValue}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
        {/* Area Chart - AI vs Human-Created Trends */}
        <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              AI vs Human-Created Content Trends
            </h2>
            <div className="flex items-center gap-2">
              <button className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md">
                Dual Chart
              </button>
              <button className="px-3 py-1 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md flex items-center gap-1">
                Content
                <ChevronDown className="w-4 h-4" />
              </button>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={MOCK_TREND_DATA}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="time" stroke="#6b7280" fontSize={12} />
              <YAxis stroke="#6b7280" fontSize={12} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1f2937',
                  borderRadius: '8px',
                  border: '1px solid #374151',
                  color: '#fff',
                }}
              />
              <Area
                type="monotone"
                dataKey="ai"
                stroke="#3182ce"
                fill="#3182ce"
                fillOpacity={0.1}
                strokeWidth={2}
                name="AI vs Human-Created"
              />
              <Area
                type="monotone"
                dataKey="human"
                stroke="#38a169"
                fill="#38a169"
                fillOpacity={0.1}
                strokeWidth={2}
                name="AI vs Human"
              />
            </AreaChart>
          </ResponsiveContainer>
          <div className="flex items-center justify-center gap-6 mt-4">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-blue-600" />
              <span className="text-sm text-gray-600 dark:text-gray-400">
                AI vs Human-Created
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-emerald-600" />
              <span className="text-sm text-gray-600 dark:text-gray-400">
                AI vs Human
              </span>
            </div>
          </div>
        </div>

        {/* Pie Chart - Content Type Distribution */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
            Content Type Distribution
          </h2>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie
                data={MOCK_CONTENT_DISTRIBUTION}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {MOCK_CONTENT_DISTRIBUTION.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1f2937',
                  borderRadius: '8px',
                  border: '1px solid #374151',
                  color: '#fff',
                }}
              />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex items-center justify-center gap-4 mt-4">
            {MOCK_CONTENT_DISTRIBUTION.map((item) => (
              <div key={item.name} className="flex items-center gap-2">
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: item.color }}
                />
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {item.name}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent Activity Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Recent Activity
          </h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <th className="text-left py-4 px-6">
                  <button
                    onClick={() => handleSort('fileName')}
                    className="flex items-center gap-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                  >
                    File Name
                    {sortField === 'fileName' &&
                      (sortOrder === 'asc' ? (
                        <ChevronUp className="w-4 h-4" />
                      ) : (
                        <ChevronDown className="w-4 h-4" />
                      ))}
                  </button>
                </th>
                <th className="text-left py-4 px-6">
                  <button
                    onClick={() => handleSort('type')}
                    className="flex items-center gap-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                  >
                    Type
                    {sortField === 'type' &&
                      (sortOrder === 'asc' ? (
                        <ChevronUp className="w-4 h-4" />
                      ) : (
                        <ChevronDown className="w-4 h-4" />
                      ))}
                  </button>
                </th>
                <th className="text-left py-4 px-6">
                  <button
                    onClick={() => handleSort('trustScore')}
                    className="flex items-center gap-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                  >
                    Trust Score
                    {sortField === 'trustScore' &&
                      (sortOrder === 'asc' ? (
                        <ChevronUp className="w-4 h-4" />
                      ) : (
                        <ChevronDown className="w-4 h-4" />
                      ))}
                  </button>
                </th>
                <th className="text-left py-4 px-6">
                  <button
                    onClick={() => handleSort('status')}
                    className="flex items-center gap-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                  >
                    Status
                    {sortField === 'status' &&
                      (sortOrder === 'asc' ? (
                        <ChevronUp className="w-4 h-4" />
                      ) : (
                        <ChevronDown className="w-4 h-4" />
                      ))}
                  </button>
                </th>
                <th className="text-left py-4 px-6">
                  <button
                    onClick={() => handleSort('date')}
                    className="flex items-center gap-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                  >
                    Date
                    {sortField === 'date' &&
                      (sortOrder === 'asc' ? (
                        <ChevronUp className="w-4 h-4" />
                      ) : (
                        <ChevronDown className="w-4 h-4" />
                      ))}
                  </button>
                </th>
                <th className="text-left py-4 px-6 text-sm font-medium text-gray-600 dark:text-gray-400">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedFiles.map((file) => (
                <tr
                  key={file.id}
                  className="border-b border-gray-100 dark:border-gray-700 last:border-0 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                >
                  <td className="py-4 px-6">
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {file.fileName}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {file.size}
                        </span>
                        <span className="text-xs text-gray-400">•</span>
                        <div className="flex items-center gap-1">
                          <span className="text-xs font-mono text-gray-500 dark:text-gray-400">
                            {file.persistentId}
                          </span>
                          <button
                            onClick={() =>
                              copyToClipboard(file.persistentId, file.id)
                            }
                            className="p-0.5 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                          >
                            {copiedId === file.id ? (
                              <Check className="w-3 h-3 text-green-600" />
                            ) : (
                              <Copy className="w-3 h-3 text-gray-400" />
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="py-4 px-6 text-sm text-gray-600 dark:text-gray-400">
                    {file.type}
                  </td>
                  <td className="py-4 px-6">
                    <span
                      className={`text-sm font-medium ${getTrustScoreColor(
                        file.trustScore
                      )}`}
                    >
                      {file.trustScore > 0 ? file.trustScore.toFixed(1) : '-'}
                    </span>
                  </td>
                  <td className="py-4 px-6">
                    <span
                      className={`inline-flex px-3 py-1 text-xs font-medium rounded-full ${getStatusBadge(
                        file.status
                      )}`}
                    >
                      {getStatusLabel(file.status)}
                    </span>
                  </td>
                  <td className="py-4 px-6 text-sm text-gray-600 dark:text-gray-400">
                    {formatDate(file.date)}
                  </td>
                  <td className="py-4 px-6">
                    <div className="relative">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveMenuId(
                            activeMenuId === file.id ? null : file.id
                          );
                        }}
                        className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                      >
                        <MoreVertical className="w-4 h-4" />
                      </button>

                      {activeMenuId === file.id && (
                        <>
                          <div
                            className="fixed inset-0 z-40"
                            onClick={() => setActiveMenuId(null)}
                          />
                          <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50">
                            <button
                              onClick={() => handleViewFile(file.id)}
                              className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-t-lg"
                            >
                              <Eye className="w-4 h-4" />
                              View Details
                            </button>
                            <button
                              onClick={() => handleDownload(file)}
                              className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                            >
                              <Download className="w-4 h-4" />
                              Download
                            </button>
                            <button
                              onClick={() => handleShare(file)}
                              className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                            >
                              <Share2 className="w-4 h-4" />
                              Share Link
                            </button>
                            <button
                              onClick={() => handleDelete(file.id)}
                              className="w-full flex items-center gap-3 px-4 py-3 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-b-lg"
                            >
                              <Trash2 className="w-4 h-4" />
                              Delete
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Empty State */}
        {sortedFiles.length === 0 && (
          <div className="text-center py-16">
            <div className="w-20 h-20 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
              <Upload className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              No files yet
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Upload your first file to get started
            </p>
            <button
              onClick={() => navigate('/analyze')}
              className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium"
            >
              Upload File
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
